import React, { useState, useCallback, useRef, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import FullScreenEditDialog from '../../shared/FullScreenEditDialog';
import SidebarTabs from '../../shared/SidebarTabs';
import GeneralTab from './tabs/GeneralTab';
import PeopleIntegrationsTab from './tabs/PeopleIntegrationsTab';
import ModulesTab from './tabs/ModulesTab';
import SettingsTab from './tabs/SettingsTab';
import AdvancedTab from './tabs/AdvancedTab';
import EntityLoadingState from '@/se/components/entity-states/EntityLoadingState';
import EntityErrorState from '@/se/components/entity-states/EntityErrorState';
import EntityEmptyState from '@/se/components/entity-states/EntityEmptyState';
import { isDefinedAndNotNull } from '@/se/utilities/check';
import { HospitalFormValue } from './types';

const CREATE_DEFAULTS: Partial<HospitalFormValue> = {
  sessionTimeout: 600, // 10 minutes
  questionnaireValidMonths: 6,
  minBracelets: 1,
  passwordRefreshDays: 90,
};

// Tab indices
const TAB_GENERAL = 0;
const TAB_SETTINGS = 3;

interface ValidationError {
  message: string;
  tab: number;
}

function validateForm(value: HospitalFormValue): ValidationError | null {
  if (!value.name || !value.name.trim()) {
    return { message: 'Name is required.', tab: TAB_GENERAL };
  }
  if (value.sessionTimeout === undefined || value.sessionTimeout === null) {
    return { message: 'Session timeout is required.', tab: TAB_SETTINGS };
  }
  if (value.questionnaireValidMonths === undefined || value.questionnaireValidMonths === null) {
    return { message: 'Questionnaire valid months is required.', tab: TAB_SETTINGS };
  }
  return null;
}

/**
 * Full-screen hospital edit/create dialog.
 * Receives the same props as EntityEdit/EntityCreate from the CRUD compose chain.
 * Works for both Create and Edit flows.
 */

interface HospitalEditDialogProps {
  // From CRUD compose chain (Edit flow)
  data?: any;
  getValue?: (data: any) => any;
  handleUpdate?: (data: any) => Promise<any>;
  handleRemove?: (id: any) => Promise<any>;
  idProvider?: (props: any) => any;
  history: any;
  backUrlProvider?: (...args: any[]) => string;
  onRemoveUrlProvider?: () => string;
  onBack?: () => void;
  Input?: any;
  // From CRUD compose chain (Create flow)
  handleCreate?: (data: any) => Promise<any>;
  entityName?: string;
  title?: string;
  initialValue?: any;
  // Shared
  Loading?: React.ComponentType;
  Error?: React.ComponentType<{ error: any }>;
  Empty?: React.ComponentType;
  width?: string;
  height?: string;
}

const HospitalEditDialog: React.FC<HospitalEditDialogProps> = props => {
  const {
    data,
    getValue,
    handleUpdate,
    handleCreate,
    handleRemove,
    idProvider,
    history,
    backUrlProvider,
    onRemoveUrlProvider,
    onBack,
    entityName,
    title: createTitle,
    initialValue: createInitialValue,
    Loading = EntityLoadingState,
    Error: ErrorComponent = EntityErrorState,
    Empty = EntityEmptyState,
  } = props;

  const isCreateMode = !!handleCreate;
  const isEditMode = !!handleUpdate;

  // Determine initial value
  const editValue = isEditMode && data && getValue ? getValue(data) : null;
  const initialValue = isCreateMode ? { ...CREATE_DEFAULTS, ...createInitialValue } : editValue;

  const [formValue, setFormValue] = useState<HospitalFormValue>(initialValue || ({} as HospitalFormValue));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const initializedRef = useRef(false);

  // Sync form value when edit data loads
  useEffect(() => {
    if (isEditMode && editValue && !initializedRef.current) {
      setFormValue(editValue);
      initializedRef.current = true;
    }
  }, [isEditMode, editValue]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else if (backUrlProvider) {
      const url = isEditMode && idProvider ? backUrlProvider(idProvider(props)) : backUrlProvider();
      if (url) {
        if (history.replace) {
          history.replace(url);
        } else {
          history.push(url);
        }
      }
    }
  }, [onBack, backUrlProvider, history, idProvider, isEditMode, props]);

  const handleSubmit = useCallback(async () => {
    const validationError = validateForm(formValue);
    if (validationError) {
      setError(validationError.message);
      setActiveTab(validationError.tab);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (isCreateMode && handleCreate) {
        await handleCreate(formValue);
      } else if (isEditMode && handleUpdate) {
        await handleUpdate(formValue);
      }
      handleBack();
    } catch (e: any) {
      const message = e?.message || 'An unexpected error has occurred.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }, [formValue, handleCreate, handleUpdate, handleBack, isCreateMode, isEditMode]);

  const handleChange = useCallback(
    (value: HospitalFormValue) => {
      setFormValue(value);
      if (error) setError(null);
    },
    [error]
  );

  const handleVoid = useCallback(async () => {
    if (!handleRemove || !idProvider) return;
    setBusy(true);
    setError(null);
    setVoidConfirmOpen(false);
    try {
      await handleRemove(idProvider(props));
      if (onRemoveUrlProvider) {
        const url = onRemoveUrlProvider();
        if (url) {
          if (history.replace) {
            history.replace(url);
          } else {
            history.push(url);
          }
        }
      } else {
        handleBack();
      }
    } catch (e: any) {
      const message = e?.message || 'Failed to void organization.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }, [handleRemove, idProvider, props, onRemoveUrlProvider, history, handleBack]);

  // Loading/error/empty states (edit mode only)
  if (isEditMode) {
    if (data?.loading) {
      return (
        <FullScreenEditDialog title="Loading..." open onClose={handleBack}>
          <Loading />
        </FullScreenEditDialog>
      );
    }
    if (data?.error) {
      return (
        <FullScreenEditDialog title="Error" open onClose={handleBack}>
          <ErrorComponent error={data.error} />
        </FullScreenEditDialog>
      );
    }
    if (!isDefinedAndNotNull(editValue)) {
      return (
        <FullScreenEditDialog title="Not Found" open onClose={handleBack}>
          <Empty />
        </FullScreenEditDialog>
      );
    }
  }

  const dialogTitle = isCreateMode
    ? createTitle || `New ${entityName || 'Hospital'}`
    : formValue.name || 'Edit Hospital';

  const submitLabel = isCreateMode ? 'Create' : 'Update';

  const tabs = [
    {
      label: 'General',
      content: <GeneralTab value={formValue} onChange={handleChange} disabled={busy} />,
    },
    {
      label: 'People & Integrations',
      content: <PeopleIntegrationsTab value={formValue} onChange={handleChange} disabled={busy} />,
    },
    {
      label: 'Modules',
      content: <ModulesTab value={formValue} onChange={handleChange} disabled={busy} />,
    },
    {
      label: 'Settings',
      content: <SettingsTab value={formValue} onChange={handleChange} disabled={busy} />,
    },
  ];

  // Only show Advanced tab in edit mode (it needs existing data)
  if (isEditMode) {
    tabs.push({
      label: 'Advanced',
      content: <AdvancedTab value={formValue} onChange={handleChange} disabled={busy} />,
    });
  }

  const voidButton =
    isEditMode && handleRemove ? (
      <Box flex={1} display="flex" alignItems="center">
        <Button
          variant="outlined"
          size="small"
          onClick={() => setVoidConfirmOpen(true)}
          disabled={busy}
          style={{ color: '#f44336', borderColor: '#f44336' }}
        >
          Void Organization
        </Button>
      </Box>
    ) : undefined;

  return (
    <>
      <FullScreenEditDialog
        title={dialogTitle}
        open
        onClose={handleBack}
        onSubmit={handleSubmit}
        submitLabel={submitLabel}
        busy={busy}
        error={error}
        extraActions={voidButton}
      >
        <SidebarTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </FullScreenEditDialog>
      <Dialog open={voidConfirmOpen} onClose={() => setVoidConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Void Organization</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to void <strong>{formValue.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleVoid} style={{ backgroundColor: '#f44336', color: '#fff' }}>
            Void
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HospitalEditDialog;
